import { PublicClientApplication, InteractionRequiredAuthError, AuthenticationResult } from "@azure/msal-browser";
import { MICROSOFT_CONFIG, MICROSOFT_SCOPES } from "./config";
import { generateState, generatePkcePair, buildAuthorizeUrl } from "./outlook-oauth";
import { MICROSOFT_WEB_REDIRECT_URI, MICROSOFT_AUTHORIZE_SCOPES } from "./config";

export class OutlookEmailService {
  private msalInstance: PublicClientApplication;
  private initialized: boolean = false;
  private initPromise: Promise<void> | null = null;
  private redirectResult: AuthenticationResult | null = null;

  constructor() {
    this.msalInstance = new PublicClientApplication(MICROSOFT_CONFIG);
  }

  async initialize() {
    if (this.initialized) return;

    // Prevent concurrent initialization
    if (this.initPromise) {
      await this.initPromise;
      return;
    }

    this.initPromise = (async () => {
      await this.msalInstance.initialize();

      // Handle any redirect response first (clears interaction state)
      // Store the result so handleRedirectResponse() can return it —
      // MSAL v3 returns null on subsequent calls after the hash is consumed
      try {
        this.redirectResult = await this.msalInstance.handleRedirectPromise();
      } catch (e) {
        console.warn('Error handling redirect promise during init:', e);
      }

      this.initialized = true;
    })();

    await this.initPromise;
  }

  /**
   * Sign in via the Outlook Web (confidential authorization-code) flow.
   * Generates PKCE + CSRF state, stashes them in sessionStorage, and navigates
   * to Microsoft /authorize on the Web redirect URI. The 90-day refresh token
   * is minted server-side by outlook-oauth-exchange — the browser never sees it.
   *
   * Reconnect passes forceFresh=true → prompt='login'. First Connect →
   * prompt='select_account'. NEVER 'consent' (re-triggers admin-consent
   * workflow on already-consented tenants — see PR #470 / outlook-oauth.ts).
   */
  async login(options: { forceFresh?: boolean } = {}) {
    const state = generateState();
    const { codeVerifier, codeChallenge } = await generatePkcePair();

    sessionStorage.setItem('outlook_oauth_state', state);
    sessionStorage.setItem('outlook_oauth_verifier', codeVerifier);

    const url = buildAuthorizeUrl({
      clientId: MICROSOFT_CONFIG.auth.clientId as string,
      redirectUri: MICROSOFT_WEB_REDIRECT_URI,
      scope: MICROSOFT_AUTHORIZE_SCOPES,
      state,
      codeChallenge,
      prompt: options.forceFresh ? 'login' : 'select_account',
    });

    window.location.href = url;
    return null;
  }

  async handleRedirectResponse() {
    await this.initialize();

    // Return the result captured during initialization — calling
    // handleRedirectPromise() again would return null since the
    // URL hash was already consumed by initialize()
    const result = this.redirectResult;
    this.redirectResult = null; // Clear after reading
    return result;
  }

  async getAccessToken() {
    await this.initialize();

    const account = this.msalInstance.getAllAccounts()[0];
    if (!account) {
      throw new Error('No account found');
    }

    try {
      const tokenResponse = await this.msalInstance.acquireTokenSilent({
        scopes: MICROSOFT_SCOPES,
        account
      });
      return tokenResponse;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expired or needs re-consent, use redirect
        await this.msalInstance.acquireTokenRedirect({
          scopes: MICROSOFT_SCOPES,
          account
        });
        return null; // Won't reach here - page redirects
      }
      throw error;
    }
  }

  async refreshAccessToken(): Promise<{ accessToken: string; expiresOn: Date | null }> {
    await this.initialize();
    const account = this.msalInstance.getAllAccounts()[0];
    if (!account) {
      throw new Error('No Microsoft account found in cache');
    }
    const tokenResponse = await this.msalInstance.acquireTokenSilent({
      scopes: MICROSOFT_SCOPES,
      account,
    });
    return {
      accessToken: tokenResponse.accessToken,
      expiresOn: tokenResponse.expiresOn,
    };
  }

  async disconnect() {
    await this.initialize();

    const account = this.msalInstance.getAllAccounts()[0];
    if (account) {
      // Clear the account from MSAL cache
      await this.msalInstance.logoutPopup({
        account,
        postLogoutRedirectUri: window.location.origin + '/settings/senders',
      }).catch(() => {
        // Fallback: just clear from cache if popup fails
        this.msalInstance.setActiveAccount(null);
      });
    }
  }

  // Clear any stale interaction state (for "interaction_in_progress" errors)
  async clearInteractionState() {
    await this.initialize();
  }

  /**
   * Extract refresh token from MSAL's internal localStorage cache.
   * MSAL browser SDK doesn't expose refresh_token on AuthenticationResult,
   * but it IS stored in localStorage when offline_access scope is granted.
   * Cache key format: {homeAccountId}-login.microsoftonline.com-refreshtoken-{clientId}----
   */
  extractRefreshTokenFromCache(homeAccountId: string): string | null {
    try {
      const clientId = MICROSOFT_CONFIG.auth.clientId
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.includes(homeAccountId) && key.includes('refreshtoken') && key.includes(clientId)) {
          const cached = localStorage.getItem(key)
          if (cached) {
            const parsed = JSON.parse(cached)
            if (parsed.secret) return parsed.secret
          }
        }
      }
    } catch (e) {
      console.warn('[OutlookService] Failed to extract refresh token from MSAL cache:', e)
    }
    return null
  }
}

export const outlookService = new OutlookEmailService();
